/**
 * Size Guides Invariants (TV1-116 #11)
 *
 * Three layers for the channel-neutral size-guide domain:
 * - `normalize`  — derives `data.galleryItems` from `data.guides` so the gallery
 *   stays in sync with the readmodel without a second api command. Heavy
 *   structural-validity logic stays in the `selectIsGuideValid` selector
 *   (state.ts), matching priceList's selector-first convention.
 * - `canApply`   — FE capability gate. User-sourced privileged CRUD triggers
 *   (`crud/upsert`, `crud/delete`) require `items-publishing:size-guides:manage`
 *   (SDD §"Permission Matrix"). api-sourced `data/*` and the `crud/done`
 *   queue-drain are allowed. `url` / `ai` get nothing initially.
 * - `validate`   — surfaces the editor's structural errors (mirrors the
 *   aggregate's INV-1..3): exactly one present main column, and unique non-empty
 *   main-column values across rows.
 *
 * Capability gating reads the acting user from `meta.user`. The user-attaching
 * dispatch wrapper (`useSizeGuidesDispatch`, ticket #12) supplies it; without it
 * `can( undefined, cap )` would block every user-sourced privileged command —
 * see project memory "Capability-gated CDSE domains need a user-attaching
 * dispatch wrapper". Now that `yarn generate-all` has landed the `UpsertSizeGuide`
 * / `DeleteSizeGuide` client operations, this domain derives the required
 * capability from the named backend operation via `gateCommandByOperation` —
 * the canonical, operation-anchored pattern (parity with priceList/warehouses) —
 * rather than authoring a fixed capability KEY.
 */

import {
  CommandOperationMap,
  Invariants,
  gateCommandByOperation
} from '@commandDrivenStateEngine/core/invariants';
import {
  SizeGuideGalleryItem,
  SizeGuidesState,
  selectors
} from './state';
import { SizeGuidesCommand } from './commands';

/**
 * Display `color` for derived gallery cards — mirrors priceList's gallery
 * policy, which uses the primary fill token for every card.
 */
const GALLERY_ITEM_COLOR = 'var(--mantine-primary-color-filled)';

/**
 * Command → backend operation map for user-sourced privileged dispatches. The
 * privileged backend writes are issued from the `sizeGuideCrudPolicy` seam, so
 * the `crud/upsert` and `crud/delete` triggers are the user-facing commands to
 * gate. The required capability is DERIVED from the named client operation
 * (`serverClientOperations[name].operation` → `operationCapability`):
 * - `crud/upsert` → `UpsertSizeGuide` (items-publishing:size-guides:manage)
 * - `crud/delete` → `DeleteSizeGuide` (items-publishing:size-guides:manage)
 *
 * Single source of truth for the runtime gate (threaded into `canApply` below)
 * and for the policy's method selection (`resolveCommandName`). Only these two
 * are gated; every other user command (editor / wizard) is a local-state edit.
 */
export const commandOperations: CommandOperationMap<SizeGuidesState, SizeGuidesCommand> = {
  'sizeGuides/crud/upsert': 'UpsertSizeGuide',
  'sizeGuides/crud/delete': 'DeleteSizeGuide'
};

/**
 * Derive the gallery view-model for one canonical guide (readmodel + a derived
 * display `color`).
 */
const toGalleryItem = ( guide: SizeGuidesState['data']['guides'][number]): SizeGuideGalleryItem => ({
  ...guide,
  color: GALLERY_ITEM_COLOR
});

/**
 * Whether `galleryItems` is already the projection of `guides` — same length,
 * same ids in order. Lets normalize skip rebuilding (and avoid a fresh array
 * reference that would re-trigger downstream selectors) when nothing changed.
 */
const galleryMatchesGuides = ( state: SizeGuidesState ): boolean => {
  const { guides, galleryItems } = state.data;
  if ( guides.length !== galleryItems.length ) return false;
  for ( let i = 0; i < guides.length; i += 1 ) {
    if ( guides[i].id !== galleryItems[i].id ) return false;
  }
  return true;
};

/**
 * Normalize — keep `data.galleryItems` derived from `data.guides`. Referentially
 * stable: only rebuild the array when the guide set changed (see the
 * `Maximum update depth` note in state.ts). Structural validity is intentionally
 * NOT recomputed here — it is read on demand via `selectors.selectIsGuideValid`.
 */
const normalize = ( state: SizeGuidesState ): SizeGuidesState => {
  if ( galleryMatchesGuides( state )) return state;
  return {
    ...state,
    data: {
      ...state.data,
      galleryItems: state.data.guides.map( toGalleryItem )
    }
  };
};

/**
 * Permission control — who can execute which commands.
 *
 * - `api`  : the data subscription (page result / loading) and CRUD queue-drain.
 * - `user` : everything except the privileged CRUD triggers, which are gated by
 *            the operation each maps to in `commandOperations` (capability
 *            `items-publishing:size-guides:manage`, read from `meta.user`).
 * - `url`  : none initially (SDD §"Permission Matrix").
 * - `ai`   : none initially (SDD §"Permission Matrix").
 */
const canApply = ({
  state,
  command,
  meta
}: {
  state: SizeGuidesState;
  command: SizeGuidesCommand;
  meta: import( '@commandDrivenStateEngine/core/types' ).CommandMeta;
}): boolean => {
  const { source } = meta;
  const { type } = command;

  // API source: readmodel data (page result / loading) and the CRUD queue-drain.
  if ( source === 'api' ) {
    return (
      type.startsWith( 'sizeGuides/data/' ) ||
      type === 'sizeGuides/crud/done'
    );
  }

  // User source: editor selection + list view-state freely; privileged CRUD
  // triggers are gated by the backend operation they map to in
  // `commandOperations` (a command absent from the map passes through).
  // `crud/done` is api-only (it drains the queue), never a user action.
  if ( source === 'user' ) {
    if ( type === 'sizeGuides/crud/done' ) return false;
    return gateCommandByOperation( commandOperations, { state, command, meta });
  }

  // URL / AI: no commands authorized for this domain initially.
  return false;
};

/**
 * Validate — structural errors for the editor's working guide, surfaced for UI
 * feedback. Mirrors the aggregate's INV-1..3:
 * - exactly one main column (`mainColumnKey` set and present in `columns`);
 * - every row has a non-empty main-column value;
 * - main-column values are unique across rows.
 * Returns `null` when no guide is loaded (nothing to validate) or when valid.
 */
const validate = ( state: SizeGuidesState ): Record<string, string> | null => {
  const { mainColumnKey, columns, rows } = state.editor;

  // No working guide loaded — nothing to validate.
  if ( mainColumnKey === null && columns.length === 0 && rows.length === 0 ) {
    return null;
  }

  const errors: Record<string, string> = {};

  // INV-1: exactly one main column, set and present in columns.
  if ( mainColumnKey === null ) {
    errors.mainColumn = 'Debe seleccionar una columna principal';
  } else {
    const mainColumns = columns.filter(( column ) => column.id === mainColumnKey );
    if ( mainColumns.length === 0 ) {
      errors.mainColumn = 'La columna principal no existe en la guía';
    } else if ( mainColumns.length > 1 ) {
      errors.mainColumn = 'Solo puede haber una columna principal';
    }
  }

  // INV-2 / INV-3: unique, non-empty main values across rows (only checkable
  // once a valid main column is known).
  if ( errors.mainColumn === undefined && mainColumnKey !== null ) {
    const seen = new Set<string>();
    for ( const row of rows ) {
      const value = row.values[mainColumnKey];
      if ( value === undefined || value === null || value === '' ) {
        errors.rows = 'Todas las filas deben tener un valor en la columna principal';
        break;
      }
      const stringValue = String( value );
      if ( seen.has( stringValue )) {
        errors.rows = 'Los valores de la columna principal no pueden repetirse';
        break;
      }
      seen.add( stringValue );
    }
  }

  return Object.keys( errors ).length > 0 ? errors : null;
};

export const invariants: Invariants<SizeGuidesState, SizeGuidesCommand> = {
  normalize,
  canApply,
  validate
};

// Re-export the structural-validity selector for callers that need a single
// boolean (e.g. enabling the editor's save button) without inspecting errors.
export { selectors };
